import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  updateClientFullProfile, updateTrainerFullProfile,
  changePassword, updateNotificationPrefs, deleteAccount,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { getInitials } from '../lib/utils';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Settings, KeyRound, Bell, ShieldAlert, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-[#C0A062]" />
      <h2 className="text-white font-semibold">{title}</h2>
    </div>
    {children}
  </div>
);

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading, isAuthenticated, isTrainer, refreshUser, logoutUser } = useAuth();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleting, setDeleting] = useState(false);

  React.useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setPhone(user.phone || '');
      setPrefs(user.notification_prefs || { email_bookings: true, email_reminders: true, email_marketing: false });
    }
  }, [user]);

  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!prefs) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { full_name: fullName, phone };
      if (isTrainer) await updateTrainerFullProfile(payload);
      else await updateClientFullProfile(payload);
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error('New password must be at least 8 characters.');
    if (newPw !== confirmPw) return toast.error('New passwords do not match.');
    setPwSaving(true);
    try {
      const r = await changePassword(curPw, newPw);
      toast.success(r.data.message);
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwSaving(false); }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await updateNotificationPrefs(next);
      toast.success('Preferences saved');
    } catch {
      setPrefs(prefs);
      toast.error('Failed to save preferences');
    }
  };

  const handleDelete = async () => {
    if (!deletePw) return toast.error('Enter your password to confirm.');
    setDeleting(true);
    try {
      const r = await deleteAccount(deletePw);
      toast.success(r.data.message);
      logoutUser();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const photo = user?.trainer_profile?.profile_image || user?.client_profile?.profile_image;

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="account-settings-page">
      <SEO title="Account Settings | DojoDash" description="Manage your DojoDash account, password, and notification preferences." />
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-[#C0A062]" />
          <h1 className="text-3xl font-bold text-white">Account Settings</h1>
        </div>

        {/* Summary */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="profile-summary-card">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 border border-zinc-700">
              {photo && <AvatarImage src={photo} alt={user.full_name} />}
              <AvatarFallback className="bg-zinc-800 text-[#C0A062] text-lg font-bold">
                {getInitials(user.full_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white font-semibold text-lg" data-testid="profile-name">{user.full_name}</p>
              <p className="text-zinc-400 text-sm" data-testid="profile-email">{user.email}</p>
              <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#C0A062]/15 border border-[#C0A062]/40 text-[#C0A062]" data-testid="profile-role">
                {isTrainer ? 'Coach' : user.role}
              </span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            Looking for your full profile (photo, bio, stats)? Head to{' '}
            <Link to="/profile" className="text-[#C0A062] hover:underline" data-testid="account-to-profile-link">My Profile</Link>
            {' '}and use "Edit profile".
          </p>
        </div>

        {/* Basic info */}
        <Section icon={Settings} title="Basic info" testId="profile-edit-card">
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label htmlFor="full-name" className="text-zinc-300">Display name</Label>
              <Input id="full-name" value={fullName} onChange={(e) => setFullName(e.target.value)}
                className="mt-2 bg-zinc-800/50 border-zinc-700 text-white" required data-testid="profile-fullname-input" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-zinc-300">Phone number</Label>
              <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="mt-2 bg-zinc-800/50 border-zinc-700 text-white" placeholder="+1 555 000 0000" data-testid="profile-phone-input" />
            </div>
            <Button type="submit" disabled={saving} className="btn-primary" data-testid="profile-save-btn">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </form>
        </Section>

        {/* Password */}
        <Section icon={KeyRound} title="Change password" testId="change-password-card">
          <form onSubmit={handlePassword} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Current password</Label>
              <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)}
                className="mt-2 bg-zinc-800/50 border-zinc-700 text-white" required data-testid="current-password-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">New password</Label>
                <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="mt-2 bg-zinc-800/50 border-zinc-700 text-white" required data-testid="new-password-input" />
              </div>
              <div>
                <Label className="text-zinc-300">Confirm new password</Label>
                <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  className="mt-2 bg-zinc-800/50 border-zinc-700 text-white" required data-testid="confirm-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={pwSaving} className="btn-primary" data-testid="change-password-btn">
              {pwSaving ? 'Updating…' : 'Update password'}
            </Button>
            <p className="text-xs text-zinc-500">Changing your password signs you out of all other devices.</p>
          </form>
        </Section>

        {/* Notifications */}
        <Section icon={Bell} title="Notification preferences" testId="notification-prefs-card">
          <div className="space-y-4">
            {[
              { key: 'email_bookings', label: 'Booking emails', desc: 'Confirmations and updates for your sessions' },
              { key: 'email_reminders', label: 'Session reminders', desc: 'Reminders before upcoming sessions' },
              { key: 'email_marketing', label: 'News & offers', desc: 'Occasional product updates and promotions' },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-white">{row.label}</p>
                  <p className="text-xs text-zinc-500">{row.desc}</p>
                </div>
                <Switch checked={prefs[row.key]} onCheckedChange={() => togglePref(row.key)} data-testid={`pref-${row.key}`} />
              </div>
            ))}
          </div>
        </Section>

        {/* Privacy */}
        <Section icon={ShieldAlert} title="Privacy" testId="privacy-controls-card">
          <p className="text-sm text-zinc-400">
            Read our <Link to="/privacy" className="text-[#C0A062] hover:underline">Privacy Policy</Link> to see what
            we collect and how it&apos;s used. You can request a copy or correction of your data at{' '}
            <span className="text-zinc-300">support@dojodash.net</span>.
          </p>
        </Section>

        {/* Danger zone */}
        <div className="bg-[#18181B] border border-red-900/50 rounded-lg p-6" data-testid="danger-zone-card">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="w-4 h-4 text-red-400" />
            <h2 className="text-red-300 font-semibold">Delete account</h2>
          </div>
          <p className="text-sm text-zinc-400 mb-4">
            Permanently deletes your account. Your data is purged within 30 days. This cannot be undone.
          </p>
          <Button variant="outline" className="border-red-800 text-red-300 hover:bg-red-950/50"
            onClick={() => setDeleteOpen(true)} data-testid="delete-account-btn">
            Delete my account
          </Button>
        </div>

        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent className="bg-[#18181B] border-zinc-800 text-white" data-testid="delete-account-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                This deactivates your account immediately and purges your data within 30 days.
                Enter your password to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input type="password" value={deletePw} onChange={(e) => setDeletePw(e.target.value)}
              placeholder="Your password" className="bg-zinc-800/50 border-zinc-700 text-white" data-testid="delete-password-input" />
            <AlertDialogFooter>
              <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 bg-transparent">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); handleDelete(); }}
                disabled={deleting}
                className="bg-red-700 hover:bg-red-600 text-white"
                data-testid="confirm-delete-account-btn"
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
